import { memo } from 'react';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { createPortal } from 'react-dom';

import { cn } from '@/shadcn/lib/utils';

import { TOOLTIP_PORTAL } from '@/constant/htmlIds';
import { ResizableColHeader } from '../resizableColHeader/resizableColHeader';

type ColumnConfig = {
  key: string;
  width: number;
  header: string;
  def: {
    header: string;
  };
};

type Props = {
  height: number;
  columns: ColumnConfig[];
  reorderColumns: (result: any) => void;
  setColumnWidth: (key: string, width: number) => void;
};

/**
 * Table column headers editor
 * Drag and drop functionality and columns resizing
 */
export const ColHeadersEditor = memo(
  ({ height, columns, reorderColumns, setColumnWidth }: Props) => (
    <DragDropContext
      onDragEnd={result => {
        reorderColumns(result);
      }}
    >
      <div
        className="flex w-fit flex-row flex-nowrap border-border border-b-2 bg-card text-left text-[10px] text-muted-foreground tracking-wide"
        style={{
          height: height + 2
        }}
      >
        <Droppable
          direction="horizontal"
          droppableId="columns-headers-droppable"
        >
          {providedDroppable => (
            <div
              className="flex w-full flex-row flex-nowrap items-center"
              ref={providedDroppable.innerRef}
              {...providedDroppable.droppableProps}
            >
              {columns.map((column, index) => (
                <Draggable
                  draggableId={column.key}
                  index={index}
                  key={column.key}
                >
                  {(providedDraggable, snapshotDraggable) => {
                    const transform =
                      providedDraggable.draggableProps.style?.transform;

                    const horizontalTransform = transform
                      ? `${transform.split(',')[0]}, 0px)`
                      : transform;

                    const draggableItem = (
                      <div
                        ref={providedDraggable.innerRef}
                        {...providedDraggable.draggableProps}
                        className={cn(
                          snapshotDraggable.isDragging &&
                            'bg-primary text-left text-[10px] text-primary-foreground tracking-wide'
                        )}
                        data-is-dragging={snapshotDraggable.isDragging}
                        style={{
                          ...providedDraggable.draggableProps.style,
                          transform: snapshotDraggable.isDragging
                            ? horizontalTransform
                            : transform
                        }}
                      >
                        <ResizableColHeader
                          allowResizing={!snapshotDraggable.isDragging}
                          columnKey={column.key}
                          dragHandleProps={providedDraggable.dragHandleProps}
                          headerName={column.def.header}
                          height={height}
                          setColumnWidth={setColumnWidth}
                          width={column.width}
                        />
                      </div>
                    );
                    return snapshotDraggable.isDragging
                      ? createPortal(
                          draggableItem,
                          document.getElementById(TOOLTIP_PORTAL)!
                        )
                      : draggableItem;
                  }}
                </Draggable>
              ))}
              {providedDroppable.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  )
);

export default ColHeadersEditor;
